import {
  Badge,
  Box,
  Button,
  CheckIcon,
  CloseIcon,
  Divider,
  Drawer,
  DrawerBody,
  DrawerHeader,
  InfoGroup,
  InfoItem,
  InfoItemKey,
  InfoItemValue,
  RefreshIcon,
  Spinner,
  Text,
} from '@razorpay/blade/components';
import { useDetailsView, useSalaryComponentHelpers } from 'components/SalaryComponents/hooks';
import { ActionModals } from 'components/SalaryComponents/modals';
import { salaryComponentParams } from '../../constants';

import React, { useState } from 'react';
import { EmptyView } from '../../components/EmptyView';
import { CheckIconWrapper } from './Create/styles';
import { formatBenefitAmount } from './utils';
import useFeature from 'hooks/useFeature';
import { useQuery } from 'react-query';
import api from 'api';
import { flexibleBenefitsTaxAllSections } from 'components/WizardViews/SalaryComponents/constants';

export const FlexibleBenefitsDetailView = () => {
  const { paramId: salaryComponentId, closeDetailsView } = useDetailsView(
    salaryComponentParams.componentId,
  );
  const { openFlexibleBenefitsEditPage } = useSalaryComponentHelpers();
  const { isFbpPercentagePrecisionEnabled } = useFeature('fbpPercentagePrecision');
  const [currentActiveModal, setCurrentActiveModal] = useState<string | null>(null);
  const isOpen = !!salaryComponentId;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      api.flexibleBenefitComponents.queries.getFlexibleBenefit,
      salaryComponentId,
      isFbpPercentagePrecisionEnabled,
    ],
    queryFn: () =>
      api.flexibleBenefitComponents.queries.getFlexibleBenefit(
        salaryComponentId!,
        isFbpPercentagePrecisionEnabled,
      ),
    enabled: isOpen,
    select: (data) => {
      return data?.payrollGetFlexibleBenefitsComponent?.component;
    },
  });

  const openModal = (modal: string) => {
    setCurrentActiveModal(modal);
  };

  const closeModal = () => {
    setCurrentActiveModal(null);
  };

  const onDismiss = () => {
    closeModal();
    closeDetailsView();
  };

  const handleEditClick = () => {
    openFlexibleBenefitsEditPage(salaryComponentId!);
  };

  const isModifyDisabled = () => {
    return data?.component_space === 'tenant';
  };

  const {
    tax_behaviour,
    proration_behaviour,
    arrear_behaviour,
    salary_settings,
    declaration_settings,
    hold_settings,
    display_behaviour,
    benefit_amount_settings,
  } = data?.settings || {};

  const getDeclarationMode = () => {
    return declaration_settings?.declaration_enabled ? 'Allow declaration' : 'No declaration';
  };

  const getBenefitAmount = () => {
    const { displayValue, description } = formatBenefitAmount(
      benefit_amount_settings,
      isFbpPercentagePrecisionEnabled ? data?.response_precision : undefined,
    );
    if (displayValue === '--') {
      return '-';
    }
    return description ? `${description} - ${displayValue}` : displayValue;
  };

  const getIncludeInGrossSalary = () => {
    return salary_settings?.show_in_gross_salary
      ? 'Add to Gross Salary'
      : 'Do not add to Gross Salary';
  };

  const getBenefitType = () => {
    return salary_settings?.non_cash_benefit ? 'Non-cash benefit' : 'Cash benefit';
  };

  const getUnclaimedAmount = () => {
    return hold_settings?.hold_unclaimed_amount ? 'Hold amount' : 'Do not hold';
  };

  return (
    <>
      <Drawer isOpen={isOpen} onDismiss={onDismiss} zIndex={900}>
        {isLoading && (
          <DrawerBody>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
              gap="spacing.5">
              <Spinner accessibilityLabel="loading flexible benefits component details" />
              <Text>Loading details</Text>
            </Box>
          </DrawerBody>
        )}
        {isError && (
          <DrawerBody>
            <EmptyView
              description={
                <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
                  Due to an unknown error, we could not load the component details page.
                </Text>
              }>
              <Box display={'flex'} justifyContent={'center'}>
                <Box width={'104px'}>
                  <Button icon={RefreshIcon} size={'small'} onClick={() => refetch()}>
                    Retry
                  </Button>
                </Box>
              </Box>
            </EmptyView>
          </DrawerBody>
        )}
        {data && !isLoading && !isError && (
          <DrawerHeader
            title={data.name}
            titleSuffix={
              <Badge color={data.is_active ? 'positive' : 'neutral'}>
                {data.is_active ? 'Active' : 'Disabled'}
              </Badge>
            }
            leading={null}>
            <Text size="small" color="surface.text.gray.muted" marginTop="spacing.2">
              {data?.description || ''}
            </Text>
          </DrawerHeader>
        )}
        {data && !isLoading && !isError && (
          <DrawerBody>
            <Box display="flex" flexDirection="column" gap="spacing.6" marginBottom={'80px'}>
              <Box display="flex" flexDirection="column" gap="spacing.4">
                <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                  Basic
                </Text>
                <Box display="flex" justifyContent={'space-between'}>
                  <Text size="medium" weight="semibold" color="surface.text.gray.muted">
                    Display name
                  </Text>
                  <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                    {display_behaviour?.display_name || data.name || ''}
                  </Text>
                </Box>
                <InfoGroup valueAlign="right" itemOrientation="horizontal" size="medium">
                  <InfoItem>
                    <InfoItemKey>Include in Gross salary</InfoItemKey>
                    <InfoItemValue>{getIncludeInGrossSalary()}</InfoItemValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemKey>Benefit type</InfoItemKey>
                    <InfoItemValue>{getBenefitType()}</InfoItemValue>
                  </InfoItem>
                </InfoGroup>
              </Box>

              <Divider />

              <Box display="flex" flexDirection="column" gap="spacing.4">
                <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                  Amount
                </Text>
                <InfoGroup itemOrientation="horizontal" size="medium" valueAlign="right">
                  <InfoItem>
                    <InfoItemKey>Declaration mode</InfoItemKey>
                    <InfoItemValue>{getDeclarationMode()}</InfoItemValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemKey>Benefit amount</InfoItemKey>
                    <InfoItemValue>{getBenefitAmount()}</InfoItemValue>
                  </InfoItem>
                </InfoGroup>
              </Box>

              <Divider />

              <Box display="flex" flexDirection="column" gap="spacing.4">
                <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                  Tax
                </Text>
                <InfoGroup itemOrientation="horizontal" size="medium" valueAlign="right">
                  <InfoItem>
                    <InfoItemKey>Exempted - Old Regime</InfoItemKey>
                    <InfoItemValue>
                      <CheckIconWrapper
                        type={tax_behaviour?.tax_exempt_in_old_regime ? 'positive' : 'negative'}>
                        {tax_behaviour?.tax_exempt_in_old_regime ? (
                          <CheckIcon color={'feedback.icon.positive.intense'} />
                        ) : (
                          <CloseIcon color="feedback.icon.negative.intense" />
                        )}
                      </CheckIconWrapper>
                    </InfoItemValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemKey>Exempted - New Regime</InfoItemKey>
                    <InfoItemValue>
                      <CheckIconWrapper
                        type={tax_behaviour?.tax_exempt_in_new_regime ? 'positive' : 'negative'}>
                        {tax_behaviour?.tax_exempt_in_new_regime ? (
                          <CheckIcon color={'feedback.icon.positive.intense'} />
                        ) : (
                          <CloseIcon color="feedback.icon.negative.intense" />
                        )}
                      </CheckIconWrapper>
                    </InfoItemValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemKey>Exemption under Section</InfoItemKey>
                    <InfoItemValue>
                      {flexibleBenefitsTaxAllSections.find(
                        (section) => section.key === tax_behaviour?.tax_exemption_section,
                      )?.label || '-'}
                    </InfoItemValue>
                  </InfoItem>

                  <InfoItem>
                    <InfoItemKey>Proration</InfoItemKey>
                    <InfoItemValue>
                      {proration_behaviour?.support_proration ? 'Enabled' : 'Disabled'}
                    </InfoItemValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemKey>Arrears calculation</InfoItemKey>
                    <InfoItemValue>
                      {arrear_behaviour?.consider_in_arrear ? 'Enabled' : 'Disabled'}
                    </InfoItemValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemKey>Unclaimed amount</InfoItemKey>
                    <InfoItemValue>{getUnclaimedAmount()}</InfoItemValue>
                  </InfoItem>
                </InfoGroup>
              </Box>

              <Box
                position={'absolute'}
                bottom={'spacing.0'}
                backgroundColor={'surface.background.gray.intense'}
                width={'calc(100% - 32px)'}
                paddingX="spacing.0"
                paddingY="spacing.6">
                <Box display="flex" flexDirection="row" gap="spacing.4">
                  <Button isFullWidth onClick={handleEditClick} isDisabled={isModifyDisabled()}>
                    Modify
                  </Button>
                  <Button
                    isFullWidth
                    variant="tertiary"
                    onClick={() => openModal(data.is_active ? 'disable' : 'enable')}>
                    {data.is_active ? 'Disable' : 'Enable'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </DrawerBody>
        )}
      </Drawer>
      {data ? (
        <ActionModals
          salaryComponent={data}
          currentActiveModal={currentActiveModal}
          closeModal={closeModal}
          enableModalContent={
            <Box display={'flex'} flexDirection={'column'} gap={'spacing.5'}>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  What will happen?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  Once enabled, you can add the component to an active employee's compensation
                </Text>
              </Box>
            </Box>
          }
          disableModalContent={
            <Box display={'flex'} flexDirection={'column'} gap={'spacing.5'}>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  What will happen?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  Once enabled, it can be used to employee to save tax
                </Text>
              </Box>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  When can a component be disabled?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  This can be removed. A flexi component can always be disabled
                </Text>
              </Box>
            </Box>
          }
        />
      ) : null}
    </>
  );
};
